import React, { useRef, useState } from "react";
import { ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2, RectangleHorizontal, RectangleVertical, RotateCcw } from "lucide-react";
import { timekeepingApi } from "../../services/timekeepingApi";

/** BLUE/RED timecard scan viewer - zoom, rotate, fit-width/fit-page, reset, and fullscreen.
 * Pan (click-drag) only activates once the image is zoomed beyond its container so ordinary
 * page scrolling is never hijacked - see the `zoom > 1` guard on every drag handler. */
export const ScanViewer = ({ file, label }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fitMode, setFitMode] = useState("page"); // "page" | "width"
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [fullscreen, setFullscreen] = useState(false);
  const dragRef = useRef(null);

  const reset = () => { setZoom(1); setRotation(0); setFitMode("page"); setOffset({ x: 0, y: 0 }); };

  const startDrag = (e) => {
    if (zoom <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: offset };
  };
  const onDrag = (e) => {
    if (!dragRef.current) return;
    const { startX, startY, origin } = dragRef.current;
    setOffset({ x: origin.x + (e.clientX - startX), y: origin.y + (e.clientY - startY) });
  };
  const endDrag = () => { dragRef.current = null; };

  if (!file) {
    return (
      <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl flex items-center justify-center h-full text-xs text-gray-500" data-testid={`scan-panel-${label}-missing`}>
        {label} side not available
      </div>
    );
  }

  const content = (
    <div className="flex flex-col h-full" data-testid={`scan-panel-${label}`}>
      <div className="flex items-center justify-between mb-2 px-1 shrink-0 flex-wrap gap-1">
        <span className={`text-xs font-bold ${label === "BLUE" ? "text-blue-600" : "text-red-600"}`}>{label} SIDE</span>
        <div className="flex items-center gap-1">
          <button data-testid={`zoom-out-${label}`} onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="p-1 rounded border border-gray-200 hover:bg-gray-50" title="Zoom out"><ZoomOut size={13} /></button>
          <button data-testid={`zoom-in-${label}`} onClick={() => setZoom((z) => Math.min(3, z + 0.25))} className="p-1 rounded border border-gray-200 hover:bg-gray-50" title="Zoom in"><ZoomIn size={13} /></button>
          <button data-testid={`rotate-${label}`} onClick={() => setRotation((r) => (r + 90) % 360)} className="p-1 rounded border border-gray-200 hover:bg-gray-50" title="Rotate"><RotateCw size={13} /></button>
          <button data-testid={`fit-width-${label}`} onClick={() => { setFitMode("width"); setZoom(1); setOffset({ x: 0, y: 0 }); }} className={`p-1 rounded border hover:bg-gray-50 ${fitMode === "width" ? "border-blue-400 text-blue-600" : "border-gray-200"}`} title="Fit width"><RectangleHorizontal size={13} /></button>
          <button data-testid={`fit-page-${label}`} onClick={() => { setFitMode("page"); setZoom(1); setOffset({ x: 0, y: 0 }); }} className={`p-1 rounded border hover:bg-gray-50 ${fitMode === "page" ? "border-blue-400 text-blue-600" : "border-gray-200"}`} title="Fit page"><RectangleVertical size={13} /></button>
          <button data-testid={`reset-view-${label}`} onClick={reset} className="p-1 rounded border border-gray-200 hover:bg-gray-50" title="Reset view"><RotateCcw size={13} /></button>
          <button data-testid={`fullscreen-toggle-${label}`} onClick={() => setFullscreen((v) => !v)} className="p-1 rounded border border-gray-200 hover:bg-gray-50" title="Toggle fullscreen">
            {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>
      <div
        className={`flex-1 bg-gray-50 rounded-lg ${fitMode === "width" ? "overflow-auto" : "overflow-hidden flex items-center justify-center"} ${zoom > 1 ? (dragRef.current ? "cursor-grabbing" : "cursor-grab") : "cursor-default"}`}
        onMouseDown={startDrag}
        onMouseMove={onDrag}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        data-testid={`scan-viewport-${label}`}
      >
        <img
          src={timekeepingApi.getFileImageUrl(file.id)}
          alt={`${label} scan`}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom})`,
            transition: dragRef.current ? "none" : "transform 0.15s",
            width: fitMode === "width" ? "100%" : "auto",
            maxWidth: fitMode === "page" ? "100%" : "none",
            maxHeight: fitMode === "page" ? "100%" : "none",
          }}
          draggable={false}
          className="select-none"
        />
      </div>
      <p className="text-[11px] text-gray-500 mt-1 px-1 truncate shrink-0">{file.original_filename} - {file.source_reference}</p>
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/90 p-6" data-testid={`scan-fullscreen-${label}`}>
        {content}
      </div>
    );
  }
  return content;
};
